<!-- eslint-disable vue/no-mutating-props -->
<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import JiraAPI from 'dashboard/api/integrations/jira';
import moment from 'moment';
import Spinner from 'shared/components/Spinner.vue';

export default {
  components: { Spinner },
  props: {
    selectedIssue: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      summary: '',
      issue: {},
      isFetching: true,
      errorWhenFetchingIssue: false,

      issueComments: [],
      isFetchingComments: true,
      errorWhenFetchingComments: false,

      maxCharsPerComment: 400,
    };
  },
  computed: {
    ...mapGetters({
      conversation: 'getSelectedChat',
      accountId: 'getCurrentAccountId',
      globalConfig: 'globalConfig/get',
    }),
    account() {
      return this.$store.getters['accounts/getAccount'](this.accountId);
    },
    additionalInfos() {
      const createdAt =
        this.$t(
          'INTEGRATION_SETTINGS.JIRA.SHOW_TICKET.ISSUE_INFOS_MODAL.CREATED'
        ) + this.formattedTime(this.issue.fields.created);
      const lastUpdate =
        this.$t(
          'INTEGRATION_SETTINGS.JIRA.SHOW_TICKET.ISSUE_INFOS_MODAL.LAST_UPDATE'
        ) + this.formattedTime(this.issue.fields.updated);
      return `${createdAt}
              ${lastUpdate}`;
    },
    fetchedData() {
      return !this.isFetching && !this.errorWhenFetchingIssue;
    },
  },
  mounted() {
    this.fetchIssueInfos();
  },
  methods: {
    onClose() {
      this.$emit('close');
    },
    async fetchIssueInfos() {
      try {
        const { data } = await JiraAPI.fetchIssueInfo(this.selectedIssue.key);
        this.isFetching = false;
        if (!data) throw new Error('Jira API error');

        this.issue = data;
        this.fetchIssueComments(); // Só busca os comentários se achou a issue
      } catch (e) {
        this.errorWhenFetchingIssue = true;
        this.isFetching = false;
        useAlert(
          this.$t(
            'INTEGRATION_SETTINGS.JIRA.CREATE_TICKET.API.FETCH_ISSUE_TYPES.ERROR_MESSAGE'
          )
        );
      }
    },
    async fetchIssueComments() {
      try {
        const { data } = await JiraAPI.fetchIssueComments(
          this.selectedIssue.key
        );
        this.isFetchingComments = false;
        if (!data) throw new Error('Jira API error');

        this.issueComments = data?.comments || [];
      } catch (e) {
        this.errorWhenFetchingComments = true;
        this.isFetchingComments = false;
        useAlert(
          this.$t(
            'INTEGRATION_SETTINGS.JIRA.SHOW_TICKET.ISSUE_INFOS_MODAL.ERROR_TO_FETCH_COMMENTS'
          )
        );
      }
    },
    formattedTime(time) {
      return moment(time).format('MMM D YYYY, h:mm A');
    },
    formattedComment(content) {
      let text = content

        // Processa trechos de código
        .replace(/\{code:([^}]+)\}([\s\S]*?)\{code\}/g, (match, lang, code) => {
          // Escapa caracteres HTML dentro do código para segurança
          const escapedCode = code
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;');
          return `<pre><code class="language-${lang}">${escapedCode}</code></pre>`;
        })

        // Negrito: *texto*
        .replace(/\*(.*?)\*/g, '<strong>$1</strong>')

        // Sublinhado: +texto+
        .replace(/\+(.*?)\+/g, '<u>$1</u>')

        // Tachado: -texto-
        .replace(/-(\S.+?\S)-/g, '<s>$1</s>')

        // Itálico: _texto_
        .replace(/_(.*?)_/g, '<em>$1</em>')

        // Cor: {color:#codigo}texto{color}
        .replace(
          /\{color:([^}]+)\}(.*?)\{color\}/g,
          '<span style="color:$1">$2</span>'
        )

        // Quebras de linha
        .replace(/(\r\n|\n|\r)/g, '<br />');

      // Retorna o texto processado sem envolver em <p> tags
      return text;
    },
  },
};
</script>

<template>
  <div>
    <div
      class="pt-8 pb-6 pr-14 pl-8 flex gap-2 items-center w-full justify-between text-lg"
    >
      <div class="flex gap-2 items-center font-bold">
        <div v-if="fetchedData" class="flex gap-1 items-center text-sm">
          <img
            v-tooltip.bottom="selectedIssue.issueTypeName"
            class="w-5 h-5"
            :src="selectedIssue.iconUrl"
            :alt="selectedIssue.name"
          />
        </div>
        <span>{{ selectedIssue.key }}</span>
        <fluent-icon
          v-if="fetchedData"
          v-tooltip.bottom="additionalInfos"
          icon="info"
          size="18px"
          class="icon"
        />
      </div>
      <!-- <div class="flex w-full gap-5">
      </div> -->

      <!-- START ISSUE TYPE SECTION -->
      <div class="flex flex-col items-center justify-center">
        <span class="text-xxs leading-none">
          {{
            $t(
              'INTEGRATION_SETTINGS.JIRA.SHOW_TICKET.ISSUE_INFOS_MODAL.ASSIGNEE'
            )
          }}
        </span>
        <span v-if="fetchedData" class="text-sm">
          {{ issue.fields.assignee ? issue.fields.assignee.displayName : '-' }}
        </span>
        <Spinner v-else />
      </div>
      <!-- END ISSUE TYPE SECTION -->
      <!-- START PRIORITY SECTION -->
      <div class="flex flex-col items-center justify-center">
        <div class="text-xxs leading-none">
          {{
            $t(
              'INTEGRATION_SETTINGS.JIRA.SHOW_TICKET.ISSUE_INFOS_MODAL.PRIORITY'
            )
          }}
        </div>
        <div v-if="fetchedData" class="flex gap-1 items-center text-sm">
          <img
            class="w-3 h-3"
            :src="issue.fields.priority.iconUrl"
            :alt="issue.fields.priority.name"
          />
          <span>
            {{ issue.fields.priority.name }}
          </span>
        </div>
        <Spinner v-else />
      </div>
      <!-- END PRIORITY SECTION -->
    </div>

    <div class="pt-0 px-8 pb-5 max-w-full">
      <hr style="margin: 0 auto" />
    </div>

    <!-- START SUMMARY SECTION -->
    <div class="pb-6 px-8 flex gap-2 items-center w-full justify-between">
      <div class="flex gap-2 items-center max-w-[75%]">
        <h5
          v-tooltip.bottom="selectedIssue.summary"
          class="max-w-full text-lg truncate"
        >
          {{ selectedIssue.summary }}
        </h5>
      </div>
      <!-- START STATUS SECTION -->
      <div
        v-if="fetchedData"
        v-tooltip.bottom="
          fetchedData ? `Status: ${issue.fields.status.name}` : 'Status'
        "
        class="border-woot-500 border border-solid text-woot-500 rounded-full py-1 px-3 items-center text-sm font-bold"
      >
        {{ issue.fields.status.name }}
      </div>
      <Spinner v-else />
      <!-- END STATUS SECTION -->
    </div>
    <!-- END SUMMARY SECTION -->
    <div
      class="flex flex-col row justify-center content-box pb-6 px-8"
      style="margin: 0 auto"
    >
      <woot-loading-state
        v-if="isFetching"
        class="text-xs"
        :message="
          $t(
            'INTEGRATION_SETTINGS.JIRA.CREATE_TICKET.API.FETCH_ISSUE_TYPES.FETCHING_MESSAGE'
          )
        "
      />
      <span
        v-else-if="errorWhenFetchingIssue"
        class="text-sm text-red-600 text-center"
      >
        {{
          $t(
            'INTEGRATION_SETTINGS.JIRA.CREATE_TICKET.API.FETCH_ISSUE_TYPES.ERROR_MESSAGE'
          )
        }}
      </span>
      <div v-else class="main-container w-full">
        <!-- START DESCRIPTION SECTION -->
        <div class="w-full">
          <div class="font-bold">
            {{
              $t(
                'INTEGRATION_SETTINGS.JIRA.SHOW_TICKET.ISSUE_INFOS_MODAL.DESCRIPTION'
              )
            }}
          </div>
          <div
            class="min-h-[8rem] text-sm border-0 p-2"
            v-html="formattedComment(issue.fields.description)"
          />
        </div>
        <!-- END DESCRIPTION SECTION -->
        <!-- START COMMENTS SECTION -->
        <div class="w-full">
          <div class="font-bold">
            {{
              $t(
                'INTEGRATION_SETTINGS.JIRA.SHOW_TICKET.ISSUE_INFOS_MODAL.COMMENTS'
              )
            }}
          </div>
          <div
            v-if="errorWhenFetchingComments"
            class="text-sm text-red-600 text-center"
          >
            {{
              $t(
                'INTEGRATION_SETTINGS.JIRA.SHOW_TICKET.ISSUE_INFOS_MODAL.ERROR_TO_FETCH_COMMENTS'
              )
            }}
          </div>
          <Spinner v-else-if="isFetchingComments" />
          <!-- LIST OF COMMENTS -->
          <div v-else-if="!issueComments.length" class="text-xs">
            {{
              $t(
                'INTEGRATION_SETTINGS.JIRA.SHOW_TICKET.ISSUE_INFOS_MODAL.NO_COMMENTS'
              )
            }}
          </div>
          <div v-else>
            <div v-for="comment in issueComments" :key="comment.id">
              <div
                class="bg-slate-100 dark:bg-slate-900 p-2 mb-2 max-w-full rounded-sm"
              >
                <div class="flex items-center gap-2">
                  <img
                    class="w-6 h-6"
                    :src="
                      comment.author.avatarUrls['16x16'] ||
                      comment.author.avatarUrls['48x48']
                    "
                    :alt="comment.author.displayName"
                  />
                  <div>
                    <span class="font-bold">
                      {{ comment.author.displayName }}
                    </span>
                    <span class="text-xs">
                      ({{ formattedTime(comment.created) }})
                    </span>
                  </div>
                </div>
                <div class="text-xs pt-1">
                  <span v-html="formattedComment(comment.body)" />
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- END COMMENTS SECTION -->
      </div>
    </div>
  </div>
</template>

<style lang="scss">
br {
  display: inline; /* Garante que <br /> não adicione espaçamento extra */
}

pre {
  background-color: #f5f5f5;
  padding: 10px;
  overflow: auto;
  border-radius: 4px;
}

code {
  font-family: Consolas, Monaco, 'Andale Mono', 'Ubuntu Mono', monospace;
  font-size: 14px;
  color: grey;
}
</style>
